import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { AppointmentTimeGrid } from '@/components/appointments/AppointmentTimeGrid';
import { OccupancyLegend, StaffOccupancyGrid } from '@/components/appointments/StaffOccupancyGrid';
import { Button } from '@/components/ui/Button';
import { useAppointmentsInRange } from '@/hooks/useAppointments';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { filterPractitionerMembers, withStickySelectedMembers } from '@/lib/practitionerRole';
import {
  addDays,
  daysFromMonday,
  endOfDay,
  startOfDay,
  startOfWeekMonday,
  weekDaysMonday,
} from '@/utils/appointmentCalendarDates';
import { occupancyWeekForStaff, workingHoursToDayMap } from '@/utils/appointmentOccupancy';
import {
  CALENDAR_COLUMN_HEADER_PX,
  fitHourHeight,
  paddedOpeningCalendarHours,
} from '@/utils/appointmentCalendarStyle';

const VISIBLE_WEEKS = 4;

export function ReferralAssignmentPanel({
  clinicId,
  assignedClinicianId,
  pending,
  onAssign,
}: {
  clinicId: string;
  assignedClinicianId: string | null;
  pending: boolean;
  onAssign: (clinicianId: string | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()));
  const [weekStaffId, setWeekStaffId] = useState<string | null>(null);
  const [weekViewAnchor, setWeekViewAnchor] = useState<Date | null>(null);

  const { data: members, isLoading: membersLoading } = useClinicMembers(clinicId);
  const { data: roles, isLoading: rolesLoading } = useClinicRoles(clinicId);

  const occupancyDays = useMemo(() => daysFromMonday(anchor, VISIBLE_WEEKS), [anchor]);
  const weekViewDays = useMemo(
    () => weekDaysMonday(weekViewAnchor ?? anchor),
    [weekViewAnchor, anchor],
  );
  const gridDays = weekStaffId ? weekViewDays : occupancyDays;

  const range = useMemo(
    () => ({
      start: occupancyDays[0]?.toISOString() ?? new Date().toISOString(),
      end: endOfDay(occupancyDays[occupancyDays.length - 1] ?? new Date()).toISOString(),
    }),
    [occupancyDays],
  );
  const { data: appointments, isLoading: appointmentsLoading } = useAppointmentsInRange(
    clinicId,
    range,
  );

  const practitioners = useMemo(
    () => filterPractitionerMembers(members ?? [], roles ?? []),
    [members, roles],
  );

  const dropdownMembers = useMemo(
    () => withStickySelectedMembers(practitioners, members ?? [], [assignedClinicianId]),
    [practitioners, members, assignedClinicianId],
  );

  const occupancyRows = useMemo(
    () =>
      practitioners.map((member) => ({
        id: member.user_id,
        name: member.name,
        cells: occupancyWeekForStaff({
          days: occupancyDays,
          workingHours: member.working_hours,
          appointments: appointments ?? [],
          clinicianId: member.user_id,
        }),
      })),
    [practitioners, occupancyDays, appointments],
  );

  const weekStaff = practitioners.find((m) => m.user_id === weekStaffId) ?? null;
  const weekAppointments = useMemo(
    () =>
      weekStaff ? (appointments ?? []).filter((a) => a.clinician_id === weekStaff.user_id) : [],
    [appointments, weekStaff],
  );

  const bookingHours = useMemo(
    () => (weekStaff ? workingHoursToDayMap(weekStaff.working_hours) : new Map()),
    [weekStaff],
  );

  const visibleHours = useMemo(
    () =>
      paddedOpeningCalendarHours({
        businessHours: bookingHours.values(),
        eventIsoTimes: weekAppointments.flatMap((a) => [a.starts_at, a.ends_at]),
      }),
    [bookingHours, weekAppointments],
  );

  const hourCount = Math.max(1, visibleHours.endHour - visibleHours.startHour);
  const hourHeight = fitHourHeight(520 - CALENDAR_COLUMN_HEADER_PX, hourCount);

  const rangeLabel = useMemo(() => {
    const start = gridDays[0];
    const end = gridDays[gridDays.length - 1];
    if (!start || !end) return '';
    const formatter = new Intl.DateTimeFormat(i18n.language === 'da' ? 'da-DK' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return `${start.getDate()}. – ${formatter.format(end)}`;
  }, [gridDays, i18n.language]);

  const loading = membersLoading || rolesLoading || appointmentsLoading;

  function shiftRange(direction: -1 | 1) {
    const step = weekStaffId ? 7 : VISIBLE_WEEKS * 7;
    if (weekStaffId) {
      setWeekViewAnchor((prev) => addDays(startOfWeekMonday(prev ?? anchor), direction * step));
      return;
    }
    setAnchor((prev) => addDays(startOfWeekMonday(prev), direction * step));
  }

  function openWeekView(staffId: string, date: Date) {
    setWeekStaffId(staffId);
    setWeekViewAnchor(startOfWeekMonday(date));
  }

  function closeWeekView() {
    setWeekStaffId(null);
    setWeekViewAnchor(null);
  }

  return (
    <section className="space-y-4 rounded-lg border border-ds-line bg-ds-card p-6 shadow-card">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-lg font-medium text-ds-text">{t('referrals.assignment_title')}</h2>
          <p className="mt-1 text-sm text-ds-muted">{t('referrals.occupancy_subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => shiftRange(-1)}
            aria-label={t('referrals.occupancy_prev_week')}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[12rem] text-center text-sm font-medium text-ds-text">
            {rangeLabel}
          </span>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => shiftRange(1)}
            aria-label={t('referrals.occupancy_next_week')}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-[16rem] flex-1 sm:max-w-xs">
          <label className="block text-sm font-medium text-ds-text">
            {t('referrals.field_assigned_clinician')}
          </label>
          <select
            value={assignedClinicianId ?? ''}
            onChange={(e) => onAssign(e.target.value || null)}
            disabled={pending}
            className="mt-1 block w-full rounded-md border border-ds-field bg-ds-card px-3 py-2 text-sm focus:border-ds-teal focus:outline-none focus:ring-ds-teal"
          >
            <option value="">{t('referrals.unassigned')}</option>
            {dropdownMembers.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        {!weekStaff && practitioners.length > 0 && <OccupancyLegend />}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
        </div>
      ) : weekStaff ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Button type="button" variant="secondary" size="sm" onClick={closeWeekView}>
                {t('referrals.week_view_back')}
              </Button>
              <h3 className="text-sm font-semibold text-ds-text">{weekStaff.name}</h3>
            </div>
            {assignedClinicianId !== weekStaff.user_id && (
              <Button
                type="button"
                size="sm"
                disabled={pending}
                onClick={() => onAssign(weekStaff.user_id)}
              >
                {t('referrals.assign_this_clinician')}
              </Button>
            )}
          </div>
          <div className="flex h-[36rem] flex-col">
            <AppointmentTimeGrid
              days={weekViewDays}
              appointments={weekAppointments}
              bookingHours={bookingHours}
              hourHeight={hourHeight}
              dayStartHour={visibleHours.startHour}
              dayEndHour={visibleHours.endHour}
            />
          </div>
        </div>
      ) : practitioners.length === 0 ? (
        <p className="text-sm text-ds-muted">{t('referrals.no_practitioners')}</p>
      ) : (
        <StaffOccupancyGrid
          staff={occupancyRows}
          days={occupancyDays}
          selectedStaffId={assignedClinicianId}
          onSelectStaff={openWeekView}
        />
      )}
    </section>
  );
}
